import React from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shadcn/components/ui/button';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Badge } from '@/shadcn/components/ui/badge';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface BranchNodeV2Props {
  data: FlowNodeData;
}

export const BranchNodeV2: React.FC<BranchNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
  } = data;

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteNode?.(nodeId);
  };

  const branchCount = metadata?.branchCount || 2;

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-blue-500 border-2 border-white"
      />

      <Card
        className={cn(
          'w-72 cursor-pointer transition-all duration-200 hover:shadow-md',
          isSelected && 'ring-2 ring-blue-5 ring-offset-2'
        )}
        onClick={handleNodeClick}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <GitBranch className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-grey-6">Branch</h3>
                <p className="text-xs text-grey-5">Split into multiple paths</p>
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Badge variant="default" className="text-xs bg-indigo-100 text-indigo-700">
              {branchCount} branches
            </Badge>
            <p className="text-xs text-grey-5">
              Automation will execute all branches simultaneously
            </p>
          </div>

        </CardContent>
      </Card>

      {/* Multiple output handles for branches */}
      {Array.from({ length: branchCount }, (_, index) => (
        <Handle
          key={index}
          type="source"
          position={Position.Bottom}
          id={`branch-${index}`}
          isConnectable={false}
          className="w-3 h-3 bg-indigo-500 border-2 border-white"
          style={{ left: `${20 + (index * 60 / branchCount)}%` }}
        />
      ))}

      {/* Add buttons positioned at bottom for each branch */}
      {isEditing && Array.from({ length: branchCount }, (_, index) => (
        <div
          key={index}
          className="absolute top-full mt-4 z-20"
          style={{ left: `${20 + (index * 60 / branchCount)}%`, transform: 'translateX(-50%)' }}
        >
          <Button
            variant="outline"
            size="sm"
            className="w-8 h-8 p-0 rounded-full bg-white border border-grey-3 hover:border-indigo-400 hover:bg-indigo-50 shadow-md"
            onClick={(e) => {
              e.stopPropagation(); // Prevent node selection
              const rect = e.currentTarget.getBoundingClientRect();
              data.onShowOptionBar?.(nodeId, {
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
          >
            <Plus className="h-4 w-4 text-grey-6" />
          </Button>
        </div>
      ))}
    </div>
  );
};
